import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, Observable, shareReplay, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';

export type RegistrationStatus = 'draft' | 'open' | 'closed';
export type RegistrantType = 'driver' | 'streamer';
export type RegistrationChoice = 'accept' | 'decline';

export interface MetaPair {
    meta_key: string;
    meta_value?: string | null;
    sort_order?: number;
}

export interface RegistrationWindow {
    window_id?: number;
    season_id: number;
    round_date: string;            // "YYYY-MM-DD"
    race_time?: string | null;     // race start "HH:MM"
    circuit_id?: number | null;
    circuit_name?: string | null;
    title?: string | null;
    description?: string | null;
    status: RegistrationStatus;
    opens_at?: string | null;
    closes_at?: string | null;
    created_by?: number | null;
    created_at?: string | null;
    effective_status?: RegistrationStatus;
    meta?: MetaPair[];
    driver_accept_count?: number;
    driver_decline_count?: number;
    streamer_accept_count?: number;
    streamer_decline_count?: number;
}

export interface Registration {
    registration_id?: number;
    window_id: number;
    registrant_type: RegistrantType;
    player_id?: number | null;
    streamer_id?: number | null;
    name?: string | null;
    status: RegistrationChoice;
    source?: 'web' | 'bot';
    meta?: MetaPair[];
}

export interface RosterEntry {
    registrant_type: RegistrantType;
    player_id: number | null;
    streamer_id: number | null;
    name: string;
    registration_id?: number | null;
    status?: RegistrationChoice | null;   // null = no response yet
    source?: 'web' | 'bot' | null;
    meta?: MetaPair[];
}

/** One append-only row of `race_registration_history` (admin "Verlauf" view). */
export interface RegistrationHistoryEntry {
    history_id: number;
    window_id: number;
    registrant_type: RegistrantType;
    registrant_id: number;
    name: string;
    old_status: RegistrationChoice | null;   // null = first response
    new_status: RegistrationChoice;
    source: 'web' | 'bot';
    changed_by_user_id: number | null;
    changed_by_user_name: string | null;
    during_window: number;                   // 0 = change after the window closed (late)
    created_at: string;
}

export interface RegistrationHistoryResponse {
    total: number;
    rows: RegistrationHistoryEntry[];
}

export interface CallerIdentity {
    player_id: number | null;
    streamer_id: number | null;
    eligible_as: { driver: boolean; streamer: boolean };
}

export interface ActiveRegistrationResponse {
    windows: RegistrationWindow[];
    caller?: CallerIdentity;
}

/** How long one registrationActive.php answer serves all callers (menu, guard, dashboard). */
const ACTIVE_WINDOWS_TTL_MS = 30_000;

// Root-provided so the sidebar menu, the dashboard announcement and the
// registration guard share ONE instance (and thus one cached getActiveWindows
// request) — per-component `providers: [RaceRegistrationService]` would defeat that.
@Injectable({ providedIn: 'root' })
export class RaceRegistrationService {
    private base       = environment.apiBaseUrl;
    private windowsUrl = `${this.base}/api/registrationWindowsCrud.php`;
    private regUrl     = `${this.base}/api/raceRegistration.php`;
    private activeUrl  = `${this.base}/api/registrationActive.php`;
    private rosterUrl  = `${this.base}/api/registrationRoster.php`;
    private historyUrl = `${this.base}/api/registrationHistory.php`;

    private activeWindows$: Observable<ActiveRegistrationResponse> | null = null;
    private activeFetchedAt = 0;

    constructor(private http: HttpClient) {}

    getWindows(season_id: number): Observable<RegistrationWindow[]> {
        return this.http.get<RegistrationWindow[]>(`${this.windowsUrl}?season_id=${season_id}`);
    }

    saveWindow(w: RegistrationWindow): Observable<any> {
        return w.window_id
            ? this.http.put<any>(this.windowsUrl, w)
            : this.http.post<any>(this.windowsUrl, w);
    }

    setWindowStatus(w: RegistrationWindow, status: 'open' | 'closed'): Observable<any> {
        return this.http.put<any>(this.windowsUrl, { ...w, status });
    }

    deleteWindow(window_id: number): Observable<{ success: boolean }> {
        return this.http.delete<{ success: boolean }>(this.windowsUrl, { body: { window_id } });
    }

    /**
     * Active registration windows, cached for 30 s: menu, guard and dashboard
     * announcement all ask within one navigation — one request serves them all.
     * An error empties the cache so the next caller retries.
     */
    getActiveWindows(): Observable<ActiveRegistrationResponse> {
        const now = Date.now();
        if (!this.activeWindows$ || now - this.activeFetchedAt > ACTIVE_WINDOWS_TTL_MS) {
            this.activeFetchedAt = now;
            this.activeWindows$ = this.http.get<ActiveRegistrationResponse>(this.activeUrl).pipe(
                catchError((err) => {
                    this.activeWindows$ = null;
                    return throwError(() => err);
                }),
                shareReplay({ bufferSize: 1, refCount: false }),
            );
        }
        return this.activeWindows$;
    }

    getRegistrations(window_id: number): Observable<Registration[]> {
        return this.http.get<Registration[]>(`${this.regUrl}?window_id=${window_id}`);
    }

    /** Member accept/decline. Registrant meta is admin-managed, so none is sent here. */
    setMyStatus(
        window_id: number,
        registrant_type: RegistrantType,
        status: RegistrationChoice,
    ): Observable<any> {
        return this.http.post<any>(this.regUrl, { window_id, registrant_type, status });
    }

    /** Admin: every active driver/streamer for a window with their status + meta. */
    getRoster(window_id: number): Observable<RosterEntry[]> {
        return this.http.get<RosterEntry[]>(`${this.rosterUrl}?window_id=${window_id}`);
    }

    /** Admin: set a registrant's status and/or replace their meta. */
    saveRegistrantAdmin(entry: {
        window_id: number;
        registrant_type: RegistrantType;
        player_id?: number | null;
        streamer_id?: number | null;
        status?: RegistrationChoice | null;
        meta?: MetaPair[];
    }): Observable<any> {
        return this.http.put<any>(this.rosterUrl, entry);
    }

    /**
     * Admin: the accept/decline trail for a window. Member and bot changes never reach
     * admin_audit_log, so this is the only place they are visible.
     */
    getHistory(window_id: number): Observable<RegistrationHistoryResponse> {
        return this.http.get<RegistrationHistoryResponse>(`${this.historyUrl}?window_id=${window_id}`);
    }
}
